import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCandidates, getFlowTest, startFlowTest, WizardApiError } from '../../campaign-wizard.api';
import { flowAnswersFromState } from '../../campaign-wizard.logic';
import { FlowTestResult, FlowTestStatusResponse, WizardCandidate } from '../../campaign-wizard.types';
import type { WizardController } from '../../use-campaign-wizard';
import {
  effectiveTestStatus,
  TEST_POLL_MAX_MS,
  TEST_POLL_MS,
  TEST_PREPARE_MAX_TRIES,
  testKey,
  TestStatus,
} from './flow-view.logic';

const CANDIDATE_POLL_MS = 8000;
const TEST_CANDIDATE_COUNT = 3;
const FAIL_TOO_LONG = 'Der Test dauert ungewöhnlich lange. Bitte noch einmal testen.';
const FAIL_NOT_SAVED = 'Der Ablauf ist noch nicht gespeichert. Einen Moment, dann noch einmal testen.';

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** Zustand der Karte „Flow testen“: Profile, Szenario, Ergebnis. */
export const useFlowTest = (wizard: WizardController) => {
  const { campaignId, state, account } = wizard;
  const [candidates, setCandidates] = useState<WizardCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [candidateId, setCandidateId] = useState<string>('');
  const [phase, setPhase] = useState<'writing' | 'preparing'>('writing');
  const [status, setStatus] = useState<TestStatus>('idle');
  const [result, setResult] = useState<FlowTestResult | null>(null);
  const [testedKey, setTestedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Erste Kandidaten laden; solange keine da sind, regelmäßig nachfragen (der Import läuft im Hintergrund).
  useEffect(() => {
    if (!campaignId) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const list = await getCandidates(campaignId);
        if (cancelled) return;
        const first = list.items.slice(0, TEST_CANDIDATE_COUNT);
        setCandidates(first);
        setCandidateId(prev =>
          prev && first.some(c => c.id === prev) ? prev : (first[0]?.id ?? '')
        );
        setCandidatesLoading(false);
        if (first.length === 0) timer = setTimeout(load, CANDIDATE_POLL_MS);
      } catch {
        if (cancelled) return;
        setCandidatesLoading(false);
        timer = setTimeout(load, CANDIDATE_POLL_MS * 2);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [campaignId]);

  // Die Anrede (Du/Sie) und die Wartezeiten kommen aus den Schritten 5 und 6 und stecken in den Antworten.
  const answers = useMemo(() => flowAnswersFromState(state, account), [state, account]);
  const currentKey = testKey(JSON.stringify(answers), candidateId);
  const effective = effectiveTestStatus(status, testedKey, currentKey);
  /** Nur der jüngste Lauf darf den Zustand setzen (neuer Klick, anderes Profil, Verlassen der Seite). */
  const runToken = useRef(0);

  useEffect(
    () => () => {
      runToken.current += 1;
    },
    []
  );

  const run = useCallback(async () => {
    if (!campaignId || !candidateId) return;
    const key = currentKey;
    const token = ++runToken.current;
    const alive = () => mounted.current && runToken.current === token;
    setStatus('loading');
    setError(null);
    setPhase('writing');

    const fail = (message: string) => {
      if (!alive()) return;
      setError(message);
      setStatus('error');
    };

    try {
      // Der Test läuft auf dem GESPEICHERTEN Ablauf: erst speichern, dann starten (der Server meldet „preparing“, solange er noch zieht).
      await wizard.flushSave().catch(() => undefined);
      let res: FlowTestStatusResponse = await startFlowTest(campaignId, candidateId);
      for (let tries = 1; res.status === 'preparing' && tries < TEST_PREPARE_MAX_TRIES; tries += 1) {
        if (!alive()) return;
        setPhase('preparing');
        await sleep(2000);
        if (!alive()) return;
        res = await startFlowTest(campaignId, candidateId);
      }

      const startedAt = Date.now();
      while (res.status === 'running') {
        if (!alive()) return;
        setPhase('writing');
        if (Date.now() - startedAt > TEST_POLL_MAX_MS) {
          fail(FAIL_TOO_LONG);
          return;
        }
        await sleep(TEST_POLL_MS);
        if (!alive()) return;
        try {
          res = await getFlowTest(campaignId, candidateId, res.testId);
        } catch (e) {
          // Eine einzelne Abfrage kann scheitern (Netz, Neustart des Servers); der Test läuft weiter, also weiter fragen.
          if (!alive()) return;
          if ((e as WizardApiError).status && (e as WizardApiError).status! < 500) throw e;
        }
      }

      if (!alive()) return;
      if (res.status === 'done') {
        setResult(res.result);
        setTestedKey(key);
        setStatus('done');
      } else if (res.status === 'failed') {
        fail(res.error);
      } else {
        fail(FAIL_NOT_SAVED);
      }
    } catch (e) {
      fail((e as WizardApiError).message);
    }
  }, [campaignId, candidateId, currentKey, wizard]);

  /** Anderes Profil: ein laufender Test wird verworfen (sein Ergebnis gehört zum alten Profil). */
  const pickCandidate = useCallback((id: string) => {
    runToken.current += 1;
    setStatus(prev => (prev === 'loading' ? 'idle' : prev));
    setCandidateId(id);
  }, []);

  return {
    candidates,
    candidatesLoading,
    candidateId,
    pickCandidate,
    status: effective,
    phase,
    result,
    error,
    run,
    canRun: Boolean(campaignId && candidateId) && effective !== 'loading',
  };
};
